import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, input, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';

export function createNotifier() {
    const sourceSignal = signal(0);

    return {
        notify: () => {
            sourceSignal.update(v => v + 1);
        },
        listen: sourceSignal.asReadonly(),
    };
}
/**
 * Allows viewing a form's values in template, as well as its overall validity. Also has button that logs that data in depth to console.
 *
 * REQUIRED `formElement: FormGroup | FormArray` - Form array or group to display. May need to use a shared `r1_castAs_X` pipe in template
 *
 * OPTIONAL `formName: string` - Name to give the form
 */
@Component({
    selector: 'r1-compass-lib-util-form-preview',
    imports: [CommonModule, MatExpansionModule, ReactiveFormsModule],
    template: `
        <mat-accordion>
            <mat-expansion-panel
                [expanded]="expanded()"
                id="util-form-preview-container"
                [style.border]="formElement().valid ? '2px solid green' : '2px dotted red'">
                <mat-expansion-panel-header>
                    <mat-panel-title>"{{ formName() }}" {{ formElementType() }}</mat-panel-title>
                </mat-expansion-panel-header>

                <div id="r1-util-form-preview">
                    @switch (formElementType()) {
                        @case ('FormGroup') {
                            @if (formElementType() === 'FormGroup') {
                                <pre>{{ formElement().value | json }}</pre>
                            }
                        }
                        @case ('FormArray') {
                            @for (formItem of formElement().value; track $index) {
                                <pre [attr.data-testid]="formItem">{{ formItem | json }}</pre>
                            }
                            <pre>length: {{ $any(formElement())?.length }}</pre>
                        }
                    }
                    <pre>Dirty: {{ formElement().dirty | json }}</pre>
                    <pre>Touched: {{ formElement().touched | json }}</pre>
                    <pre><span
                        [style.background-color]="formElement().valid ? '#66FF99': '#FFCCCB'">Valid: {{ formElement().valid | json }}</span></pre>
                    <button (click)="logForm()" type="button">log form</button>
                </div>
            </mat-expansion-panel>
        </mat-accordion>
    `,
    styles: [
        `
            #util-form-preview-container {
                margin: 8px;
                display: table;
            }

            #r1-util-form-preview {
                font-size: 12px;
            }
        `,
    ],
})
export class UtilFormPreviewComponent implements OnInit {
    formElement = input.required<FormGroup | FormArray>();

    readonly formName = input<string | undefined>('Untitled');

    expanded = input<boolean>(false);

    id = input<string>();

    formElementType = computed(() => (this.formElement() instanceof FormArray ? 'FormArray' : 'FormGroup'));

    ngOnInit(): void {
        this.formElement().valueChanges.subscribe(() => {
            // mess with if you want
        });

        console.warn(`Debug element present, showing ${this.formName()}`, this.formElement());
    }

    logForm(): void {
        console.group(this.formName());
        console.group('Form element');
        console.log(this.formElement());
        console.log(this.formElement().controls);
        console.log(this.formElement().value);
        console.groupEnd();

        if (this.formElementType() === 'FormArray') {
            console.group('Form iterated');
            const formArray = this.formElement() as FormArray;
            formArray.controls.forEach((ele, index) => {
                const element = ele as FormGroup;
                console.log(`index: ${index}`);
                console.log(element.controls);
                console.log(element.value);
            });
            console.groupEnd();
        } else if (this.formElementType() === 'FormGroup') {
            // this can be fleshed out later
            // console.group('Form iterated');
            // const formGroup = this.$formElement() as FormGroup;
            // Object.keys(formGroup.controls).forEach((key) => {
            //     const element = formGroup.controls[key] as FormControl;
            // });
        } else {
            console.error('Invalid form element type');
        }

        console.groupEnd();
    }
}
